import Select, { PropsValue, SingleValue, Options } from 'react-select';
import clsx from 'clsx';
import { FC, useEffect, useRef, useState } from 'react';
import { Note, Document } from './_models';
// import { Editor } from 'react-draft-wysiwyg';
// import { EditorState, convertToRaw, ContentState, convertFromHTML } from 'draft-js';
// import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
// import { stateToHTML } from 'draft-js-export-html';
// import { stateFromHTML } from 'draft-js-import-html';
// import draftToHtml from 'draftjs-to-html';
import {
  createDocument,
  updateDocument,
  downloadDocument,
  openDocument,
  useUpdateDocument,
  getNote,
  createNote,
  updateNote,
  deleteNote,
  linkNoteToEventLog,
  unlinkNoteFromEventLog,
} from '../../../helpers/api';
import { Interweave } from 'interweave';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Client, ClientProvider, INoteTemplate } from '../../../helpers/models';
import moment from 'moment';
import { Form } from 'react-router-dom';
import { useAuth } from '../../auth';
import { ConfirmDialog } from '../ConfirmDialog';

import { HtmlInput } from './HtmlInput';
import type { TiptapEditorHandle } from './TipTapEditor';
import { monitoringCaptureError } from '../../../helpers/monitoring';
import { AssetShareDialog } from './AssetShareDialog';
import { useAlert } from 'react-alert';
import { NoteTemplateSelect } from './NoteTemplateSelect';
import { isHtmlEmpty } from '../../../../_metronic/helpers';
import { useReactToPrint } from 'react-to-print';
import { LinkToSessionModal, SessionChoice } from './LinkToSessionModal';

type Props = {
  client: Client;
  clientProvider?: ClientProvider;
  note?: Note;
  onChange?: (newValue: any) => void;
  onCancel?: (orphanDraftUuid?: string) => void;
  onSave?: (note?: Note) => void;
  onUpdate?: (deleted?: boolean) => void;
  disabled?: boolean;
  embedded?: boolean;
  defaultTitle?: string;
  allowLinkToSession?: boolean;
};

const NoteCard: FC<Props> = (props) => {
  const { currentUser, currentProvider } = useAuth();
  // linking a note to a session writes the event log, which only a provider may do (decision Q1)
  const allowLinkToSession = props.allowLinkToSession && !!currentProvider;
  const [note, setNote] = useState<Note>(() => {
    if (props.note) return props.note;
    return {
      uuid: undefined,
      provider: props.clientProvider?.provider || currentProvider,
      title: props.defaultTitle || undefined,
    };
  });
  const [editMode, setEditMode] = useState(!note || !note.uuid);
  const [processingAttachment, setProcessingAttachment] = useState(false);
  const [processingNote, setProcessingNote] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showLinkSessionModal, setShowLinkSessionModal] = useState(false);
  const [noteTemplate, setNoteTemplate] = useState<INoteTemplate>();
  const alert = useAlert();
  const [autosaveStatus, setAutosaveStatus] = useState<string>('');
  const autosaveTimer = useRef<NodeJS.Timeout | null>(null);
  const attachmentButton = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });
  const [deleted, setDeleted] = useState(false);
  const noteRef = useRef(note);
  useEffect(() => { noteRef.current = note; }, [note]);
  const pendingShareSaveRef = useRef<Note | null>(null);
  // The editor's onChange is debounced, so note.content lags what was typed.
  // Every save reads the editor directly (WST-983).
  const editorApi = useRef<TiptapEditorHandle>(null);
  const editorContent = (fallback?: string) => editorApi.current?.getHTML() ?? fallback;
  // Set while Save / Save & Share is in flight and until edit mode is entered
  // again: blocks autosave drafts (a late draft PATCH would resurrect a stale
  // draft) and late editor onChange (the card must show what was saved).
  const editLockedRef = useRef(false);
  const draftInFlight = useRef<Promise<void> | null>(null);
  useEffect(() => {
    if (editMode) editLockedRef.current = false;
  }, [editMode]);

  // When note or props.note changes, reset state
  useEffect(() => {
    if (props.note) {
      // Never overwrite local state while editing the same note —
      // a background refetch would replace in-progress edits with stale server data.
      if (editMode && note.uuid && note.uuid === props.note.uuid) {
        return;
      }
      // Right after a local save, our state is fresher than props.note until
      // the parent's refetch lands. Keep ours if its updated_at is newer —
      // otherwise e.g. a Save after "Edit from Draft" would be clobbered by
      // the stale list entry and the old draft banner would reappear.
      if (
        note.uuid &&
        note.uuid === props.note.uuid &&
        note.updated_at &&
        props.note.updated_at &&
        new Date(note.updated_at).getTime() > new Date(props.note.updated_at).getTime()
      ) {
        return;
      }
      setNote(props.note);
    }
  }, [props.note, editMode, note.uuid]);

  const saveDraft = () => {
    if (editLockedRef.current) return Promise.resolve();
    const run = saveDraftNow().finally(() => {
      if (draftInFlight.current === run) draftInFlight.current = null;
    });
    draftInFlight.current = run;
    return run;
  };

  const saveDraftNow = async () => {
    const curr = { ...noteRef.current, content: editorContent(noteRef.current.content) };
    const isContentSame = curr.content === undefined || curr.content === curr.draft_content;
    const isTitleSame = curr.title === undefined || curr.title === curr.draft_title;
    if (deleted) return;
    if (isContentSame && isTitleSame) return;
    if (!curr.uuid) {
      // Don't create a backend note for an empty draft (no content, no user-entered title beyond the default).
      const contentEmpty = isHtmlEmpty(curr.content);
      const titleEmpty = !curr.title?.trim() || curr.title === props.defaultTitle;
      if (contentEmpty && titleEmpty) return;
      try {
        const created = await createNote(props.client, {
          ...curr,
          draft_content: curr.content,
          draft_title: curr.title,
          content: undefined,
        });
        // Visible to a Save that awaited this draft before the next render.
        noteRef.current = { ...noteRef.current, uuid: created.uuid };
        setNote(prev => ({ ...prev, uuid: created.uuid, draft_content: curr.content, draft_title: curr.title }));
        setAutosaveStatus(`Draft autosaved ${moment(new Date()).format('LLL')}`);
      } catch (err) {
        //alert.error('Failed to autosave draft.');
      }
    } else {
      // If note exists, always update draft_content using updateNote
      try {
        const updated = await updateNote(props.client, { uuid: curr.uuid, draft_content: curr.content, draft_title: curr.title });
        setAutosaveStatus(`Draft autosaved ${moment(new Date()).format('LLL')}`);
      } catch (err) {
        //alert.error('Failed to autosave draft.');
      }
    }
  };

  // Autosave draft when editing
  useEffect(() => {
    if (!editMode) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      await saveDraft();
    }, 5000); // 5 seconds
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [note.content, note.title, editMode, note.uuid]);

  // Autosave on window blur (losing focus)
  useEffect(() => {
    if (!editMode) return;
    const handleBlur = async () => {
      await saveDraft();
    };
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('blur', handleBlur);
    };
  }, [editMode, note.content, note.title, note.uuid]);

  // Show 'Restore Autosave' if draft_content exists and not in edit mode
  const showDraftInfo = !editMode && note?.draft_content && note.draft_content !== note.content;

  // Handler to restore draft (edit the draft)
  const handleRestoreDraft = () => {
    setEditMode(true);
    setNote({ ...note, content: note.draft_content, title: note.draft_title || note.title });
  };

  // On save, update content and clear draft_content
  // Lock out autosave and late onChange, let an in-flight draft finish (it may
  // be creating this note), then build the payload from the live editor.
  const beginSave = async (): Promise<Note> => {
    const content = editorContent(noteRef.current.content);
    editLockedRef.current = true;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    if (draftInFlight.current) await draftInFlight.current;
    return { ...noteRef.current, content, draft_content: '' };
  };

  const handleSave = async () => {
    if (note) {
      setProcessingNote(true);
      const noteToSave = await beginSave();
      try {
        let updatedNote;
        if (noteToSave.uuid) {
          updatedNote = await updateNote(props.client, noteToSave);
        } else {
          updatedNote = await createNote(props.client, noteToSave);
        }
        setNote(updatedNote);
        setProcessingNote(false);
        setEditMode(false);
        props.onSave && props.onSave(updatedNote);
      } catch (err) {
        editLockedRef.current = false;
        setProcessingNote(false);
        alert.error('Failed to save note.');
      }
    }
  };

  // Save draft and exit edit mode
  const handleSaveDraft = async () => {
    if (note?.uuid) {
      setProcessingNote(true);
      try {
        const updated = await updateNote(props.client, {
          uuid: note.uuid,
          draft_content: editorContent(note.content),
          draft_title: note.title,
        });
        setNote(updated);
        setAutosaveStatus(`Autosaved ${moment(new Date()).format('LLL')}`);
        setEditMode(false);
        setProcessingNote(false);
        props.onSave && props.onSave(updated);
      } catch (err) {
        setProcessingNote(false);
        alert.error('Failed to save draft.');
      }
    }
  };

  const handleCancel = () => {
    if (note.uuid && props.note?.uuid) {
      // Editing an existing saved note — reload from server and drop edits.
      getNote(props.client, note.uuid).then((n: Note) => setNote(n));
      setEditMode(false);
    } else if (note.uuid && !props.note?.uuid) {
      // New note that autosaved a draft on the backend before the user canceled.
      // Hand the orphan uuid back so the parent can clean it up.
      props.onCancel && props.onCancel(note.uuid);
    } else {
      props.onCancel && props.onCancel();
    }
  };

  const handleSaveAndShare = async () => {
    if (!note) return;
    setProcessingNote(true);
    const noteToSave = await beginSave();
    try {
      const updatedNote = noteToSave.uuid
        ? await updateNote(props.client, noteToSave)
        : await createNote(props.client, noteToSave);
      // Preserve local access state (e.g. values carried over from a duplication source)
      // so the share dialog opens with them pre-selected even if the backend
      // didn't persist the access fields on create.
      const merged = {
        ...updatedNote,
        client_access: note.client_access ?? updatedNote.client_access,
        guardians_access: note.guardians_access ?? updatedNote.guardians_access,
        contacts_access: note.contacts_access ?? updatedNote.contacts_access,
      };
      setNote(merged);
      setProcessingNote(false);
      setEditMode(false);
      // Defer the onSave callback until the share dialog closes. If we fire it
      // now, the parent unmounts this NoteCard (e.g. ClientNotes clears its
      // addNew slot) and the share dialog never gets a chance to render.
      pendingShareSaveRef.current = merged;
      setShowShareDialog(true);
    } catch (err) {
      editLockedRef.current = false;
      setProcessingNote(false);
      alert.error('Failed to save note.');
    }
  };

  const handleLinkToSession = async (choice: SessionChoice) => {
    if (!note.uuid || !choice.eventLogUuid) {
      setShowLinkSessionModal(false);
      return;
    }
    setProcessingNote(true);
    try {
      // If the note is already attached to a different session, detach that
      // one first so the note only appears on the new session.
      const previousEventLogUuid = note.event_log?.uuid;
      if (previousEventLogUuid && previousEventLogUuid !== choice.eventLogUuid) {
        await unlinkNoteFromEventLog(previousEventLogUuid);
      }
      await linkNoteToEventLog(choice.eventLogUuid, note.uuid);
      const sessionTitle = `${moment(choice.start).format('LL')}${choice.serviceName ? ' · ' + choice.serviceName : ''}`;
      const titleIsDefaultOrEmpty =
        !note.title?.trim() || (props.defaultTitle && note.title === props.defaultTitle);
      if (titleIsDefaultOrEmpty) {
        const updated = await updateNote(props.client, { uuid: note.uuid, title: sessionTitle });
        setNote(updated);
      } else {
        // Refresh to pick up the new event_log field from the backend.
        const refreshed = await getNote(props.client, note.uuid);
        setNote(refreshed);
      }
      alert.info('Note attached to session');
      props.onUpdate && props.onUpdate(false);
    } catch (err) {
      alert.error('Failed to attach note to session.');
    } finally {
      setProcessingNote(false);
      setShowLinkSessionModal(false);
    }
  };

  const attachFile = (file: File) => {
    setProcessingAttachment(true);
    createDocument({
      client: props.client,
      doc: {
        file: file,
        provider: props.clientProvider?.provider || currentProvider,
        note: note?.uuid,
      },
    })
      .then((res) => {
        if (res && res.data) {
          props.onUpdate && props.onUpdate(false);
          getNote(props.client, note.uuid).then((note: Note) => setNote(note));
        }
      })
      .catch((error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'attachFile',
          data: { client: props.client?.uuid, file, note: note?.uuid },
        });
      })
      .finally(() => {
        setProcessingAttachment(false);
      });
  };

  const detachFile = (doc: Document) => {
    setProcessingAttachment(true);
    updateDocument({ client: props.client, doc: { ...doc, note: null } })
      .then((res) => {
        if (res && res.data) {
          props.onUpdate && props.onUpdate(false);
          setNote(res.data);
        }
      })
      .catch((error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'detachFile',
          data: { client: props.client?.uuid, doc },
        });
      })
      .finally(() => {
        setProcessingAttachment(false);
      });
  };

  const buttons = !currentProvider ? (
    <></>
  ) : editMode ? (
    <div className="d-flex align-items-center flex-wrap justify-content-end gap-2 w-100" style={{ minWidth: 0 }}>
      <div style={{ width: '320px', maxWidth: '100%' }}>
      <NoteTemplateSelect
        classNamePrefix="serviceSelect"
        className="form-control form-control-solid serviceSelect py-0"
        client={props.client}
        provider={props.clientProvider?.provider || currentProvider}
        excludeNoteUuid={note.uuid}
        noteTemplate={noteTemplate}
        onChange={(newNoteTemplate) => {
          setNoteTemplate(newNoteTemplate);
          if (isHtmlEmpty(note.content)) {
            setNote({ ...note, content: newNoteTemplate?.content || '' });
          } else {
            ConfirmDialog({
              title: `Note Template`,
              message: `Replace note content with ${newNoteTemplate?.title || ''} template content?`,
              confirmButton: 'Replace',
              confirmClassName: 'btn btn-danger btn-sm',
              cancelButton: 'Cancel',
              onProceed: (proceed) => {
                if (proceed) {
                  setNote({ ...note, content: newNoteTemplate?.content || '' });
                }
              },
            });
          }
        }}
        onSelectNote={(sourceNote) => {
          const applyContent = () => setNote({ ...note, content: sourceNote.content || '' });
          if (isHtmlEmpty(note.content)) {
            applyContent();
          } else {
            ConfirmDialog({
              title: 'Use Prior Note',
              message: `Replace current content with "${sourceNote.title || 'prior note'}"?`,
              confirmButton: 'Replace',
              confirmClassName: 'btn btn-danger btn-sm',
              cancelButton: 'Cancel',
              onProceed: (proceed) => {
                if (proceed) applyContent();
              },
            });
          }
        }}
      />
      </div>
      <div className="btn btn-outline btn-outline-primary btn-sm" onClick={handleCancel}>
        Cancel
      </div>
      <div
        className="btn btn-outline btn-outline-primary btn-sm text-nowrap"
        onClick={() => {
          saveDraft();
          setAutosaveStatus('Draft saved');
          setTimeout(() => setAutosaveStatus(''), 2000);
        }}
      >
        Save Draft
      </div>
      <button className="btn btn-primary btn-sm text-nowrap" onClick={handleSave} disabled={!note}>
        Save {processingNote && <Spinner className="ms-3" animation="border" size="sm" role="status" />}
      </button>
      <button
        className="btn btn-primary btn-sm text-nowrap"
        onClick={handleSaveAndShare}
        disabled={!note || processingNote}
      >
        Save &amp; Share
      </button>
    </div>
  ) : (
    <>
      {note.uuid && (
        <>
          <input
            type="file"
            id="file"
            ref={attachmentButton}
            style={{ display: 'none' }}
            onChange={(event) => {
              if (event) {
                event.stopPropagation();
                event.preventDefault();
                const file = event.target.files && event.target.files[0];
                if (file) {
                  attachFile(file);
                }
              }
            }}
          />
          <div
            className="btn btn-outline btn-outline-primary btn-sm me-3"
            onClick={() => {
              attachmentButton.current && attachmentButton.current.click();
            }}
          >
            Add Attachment{' '}
            {processingAttachment && <Spinner className="ms-3" animation="border" size="sm" role="status" />}
          </div>
          <div
            className="btn btn-outline btn-outline-primary btn-sm me-3"
            onClick={() => {
              setShowShareDialog(true);
            }}
          >
            Share
          </div>
          {allowLinkToSession && !note.event_log && (
            <div
              className="btn btn-outline btn-outline-primary btn-sm me-3"
              onClick={() => setShowLinkSessionModal(true)}
            >
              Attach to Session
            </div>
          )}
        </>
      )}
      {note.uuid && (<>
        <div className="btn btn-outline btn-outline-primary btn-sm me-3" onClick={() => reactToPrintFn()}>
          Print
        </div>
        <div
            className="btn btn-outline btn-outline-danger btn-sm me-3"
            onClick={() => {
              ConfirmDialog({
                title: 'Remove Note',
                message: `Remove${note.title ? ' ' + note.title : ''}?`,
                confirmButton: 'Remove',
                confirmClassName: 'btn btn-danger btn-sm',
                cancelButton: 'Cancel',
                onProceed: async (proceed) => {
                  if (proceed && note.uuid) {
                    setProcessingNote(true);
                    try {
                      await deleteNote(props.client, note);
                      setProcessingNote(false);
                      setDeleted(true);
                      alert.info('Note removed');
                      props.onUpdate && props.onUpdate(true);
                    } catch (err) {
                      setProcessingNote(false);
                      alert.error('Unable to delete note.');
                    }
                  }
                },
              });
            }}
          >
            Remove
          </div>

      </>)}
      <div
        className="btn btn-primary btn-sm me-3"
        onClick={() => {
          setEditMode(true);
        }}
      >
        {note.uuid ? 'Edit' : 'Add'}
      </div>
    </>
  );

  const visibleTo = () => {
    let lst = [];
    if (note?.provider?.parent_org?.share_org_assets) {
      lst.push(`${note?.provider?.parent_org?.name} Providers`);
    } else {
      lst.push(note?.provider?.name);
    }
    note.client_access && lst.push(note?.client?.name);
    note.guardians_access &&
      note.guardians_access?.length > 0 &&
      lst.push(...note.guardians_access?.map((g) => g.name));
    return lst.join(', ');
  };

  if (deleted) return <></>;

  return (
    <div className={clsx('card', { 'shadow-sm mt-auto mb-5': !props.embedded })} data-uuid={note.uuid}>
      <div className="card-header mobile-header flex-row-reverse">
        <div className="card-toolbar">
          {props.embedded ? (
            <button
              className="btn btn-close px-0"
              onClick={() => {
                props.onCancel && props.onCancel();
              }}
            />
          ) : (
            <>{buttons}</>
          )}
        </div>
      </div>

      <div className="card-body" ref={contentRef}>
      <h3 className="card-title me-auto mb-3 ms-1">
          {editMode ? (
            <div className="form-inline">
              <input
                type="text"
                className="form-control form-control-solid w-100 d-inline-block"
                style={{ width: note.title ? note.title?.length + 1 + 'ch' : 'auto' }}
                placeholder="Title (optional)"
                maxLength={250}
                value={note.title || ''}
                onChange={(e) => { const v = e.target.value; setNote(prev => ({ ...prev, title: v })); }}
              />
            </div>
          ) : (
            <div className="d-flex justify-content-between">
              <div>{note.title}</div>
              <div className="text-muted fs-6">{note.created_at && moment(note.created_at).format('LLL')}</div>
            </div>
          )}
        </h3>
        {!editMode && note.event_log && (
          <div className="text-muted fs-7 mb-3 ms-1 d-flex align-items-center">
            <i className="las la-calendar inherit-color me-1"></i>
            <span>
              Attached to session: {moment(note.event_log.start).format('LLL')}
              {note.event_log.provider_service_name && ` · ${note.event_log.provider_service_name}`}
            </span>
            {allowLinkToSession && (
              <i
                role="button"
                tabIndex={0}
                className="las la-edit fs-4 ms-2 cursor-pointer inherit-color"
                title="Attach to a different session"
                aria-label="Attach to a different session"
                onClick={() => setShowLinkSessionModal(true)}
              ></i>
            )}
          </div>
        )}
        {currentProvider && showDraftInfo && (
          <div className="row">
            <Alert variant="warning" className="me-3 col-12">
              <div className="d-flex align-items-center justify-content-between">
                You have an unsaved draft.
                <div>
                  <button className="btn btn-outline btn-outline-primary btn-sm ms-3" disabled={processingNote} onClick={async () => {
                    setProcessingNote(true);
                    try {
                      const updated = await updateNote(props.client, { uuid: note.uuid, content: note.draft_content, title: note.draft_title || note.title, draft_content: '', draft_title: '' });
                      setNote(updated);
                      setProcessingNote(false);
                      props.onSave && props.onSave(updated);
                    } catch (err) {
                      setProcessingNote(false);
                      alert.error('Failed to save draft.');
                    }
                  }}>
                    Save Draft {processingNote && <Spinner animation="border" size="sm" role="status" />}
                  </button>
                  <button className="btn btn-outline btn-outline-primary btn-sm ms-3" onClick={handleRestoreDraft}>
                    Edit from Draft
                  </button>
                  <button className="btn btn-outline btn-outline-primary btn-sm ms-3" onClick={() => {
                    ConfirmDialog({
                      title: 'Discard Draft',
                      message: 'Are you sure you want to discard this draft?',
                      confirmButton: 'Discard',
                      confirmClassName: 'btn btn-danger btn-sm',
                      cancelButton: 'Cancel',
                      onProceed: async (proceed) => {
                        if (proceed) {
                          setProcessingNote(true);
                          const newNote = await updateNote(props.client, { uuid: note.uuid, draft_content: '', draft_title: '' });
                          setNote(newNote);
                          setProcessingNote(false);
                        }
                      },
                    });
                  }}>
                    Discard Draft
                  </button>
                </div>
              </div>
              <HtmlInput
                content={note.draft_content || ''}
                editMode={false}
                className="providerNotes mt-3"
                placeholder=""
                onChange={(content) => {}}
              />
            </Alert>
          </div>
        )}
        <HtmlInput
          content={note.content || ''}
          editMode={editMode}
          editorRef={editorApi}
          className="providerNotes"
          placeholder=""
          formattingOptions={[
            'inline',
            'fontSize',
            'fontFamily',
            'blockType',
            'list',
            'emoji',
            'image',
            'link',
            'history',
          ]}
          onChange={(content) => {
            if (editLockedRef.current) return;
            setNote(prev => ({ ...prev, content }));
          }}
          provider={props.clientProvider?.provider || currentProvider}
          client={props.client}
        />
        {note?.documents?.map((doc) => (
          <div className="btn btn-outline btn-outline-primary btn-sm me-3 px-2 my-2" key={doc.uuid}>
            <span
              onClick={() => {
                openDocument({ client: props.client, doc });
              }}
            >
              {doc.filename}
            </span>
            {currentProvider && (
              <i
                className="fs-3 ms-4 las la-times inherit-color"
                onClick={() => {
                  ConfirmDialog({
                    title: 'Remove Attachment',
                    message: `Remove ${doc.filename}?`,
                    confirmButton: 'Remove',
                    confirmClassName: 'btn btn-danger btn-sm',
                    cancelButton: 'Cancel',
                    onProceed: (proceed) => {
                      if (proceed) {
                        detachFile(doc);
                      }
                    },
                  });
                }}
              />
            )}
          </div>
        ))}
      </div>
      <div className="card-footer py-3 d-flex justify-content-between flex-wrap">
        <div>
          {note.updated_at && (
            <div className="text-gray-600 me-2">Updated {moment(note.updated_at).format('LLL')}</div>
          )}
          {autosaveStatus && (
            <div className="text-gray-600 me-2">{autosaveStatus}</div>
          )}
        </div>
        <div className="text-gray-800">
          <i className="fas fa-lock me-1"></i> Visible to {visibleTo()}
        </div>
        {props.embedded && buttons && (
          <div className="d-flex flex-wrap justify-content-end mt-5 w-100 gap-2">
            {props.embedded && buttons}
          </div>
        )}
      </div>
      {showLinkSessionModal && (
        <LinkToSessionModal
          client={props.client}
          show={showLinkSessionModal}
          onCancel={() => setShowLinkSessionModal(false)}
          onSubmit={handleLinkToSession}
          referenceDate={note.event_log?.start ? new Date(note.event_log.start) : undefined}
        />
      )}
      {showShareDialog && (
        <AssetShareDialog
          show={showShareDialog}
          client={props.client}
          provider={props.clientProvider?.provider || currentProvider}
          asset={note}
          title="Share Note"
          description="Share this note with:"
          guardianHint="Parents only see notes they're ticked on."
          onCancel={() => {
            setShowShareDialog(false);
            const pending = pendingShareSaveRef.current;
            pendingShareSaveRef.current = null;
            if (pending) props.onSave && props.onSave(pending);
          }}
          onSaveOnly={() => {
            setShowShareDialog(false);
            const pending = pendingShareSaveRef.current;
            pendingShareSaveRef.current = null;
            if (pending) props.onSave && props.onSave(pending);
          }}
          onSubmit={(access) => {
            const newNote = { ...note, ...access };
            setNote(newNote);
            if (newNote?.uuid) {
              setProcessingNote(true);
              updateNote(props.client, newNote).finally(() => setProcessingNote(false));
            }
            setShowShareDialog(false);
            const pending = pendingShareSaveRef.current;
            pendingShareSaveRef.current = null;
            if (pending) props.onSave && props.onSave({ ...pending, ...access });
          }}
        />
      )}
    </div>
  );
};

export { NoteCard };
